import { css } from 'aphrodite';
import { useSalesLiveCoachingConversationsQuery } from '../../../queries/AIConversation/useSalesLiveCoachingConversationsQuery';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { CoachingAdherenceScorecard } from '../../components/CallRecordingDetails/components/CoachingAdherenceScorecard';
import { styleSheet } from './styles';

interface IProps {
	conversationId: string | null;
}

/**
 * Renders the coaching ADHERENCE scorecard for the selected conversation, below the transcript on
 * the Coffee AI coaching review detail pane.
 *
 * The scored phone call is joined onto each conversation by the list query
 * (`aida/coffee/coaching/filter`), so we read it straight from that already-loaded data — no
 * separate request. `CoachingAdherenceScorecard` itself short-circuits to `null` for users without
 * manager permissions, so on this internal review surface the scorecard only appears for
 * managers/admins.
 */
export function AdherencePanel({ conversationId }: IProps) {
	const { data, isLoading } = useSalesLiveCoachingConversationsQuery();

	// Nothing selected: render nothing (the transcript panel owns the "select a conversation" prompt).
	if (!conversationId) {
		return null;
	}

	if (isLoading) {
		return (
			<section className={css(styleSheet.adherence)} aria-label='Coaching adherence'>
				<h3 className={css(styleSheet.adherenceTitle)}>Coaching Adherence</h3>
				<div className={css(styleSheet.adherenceSpinner)}>
					<LoadingSpinner type='small' />
				</div>
			</section>
		);
	}

	const phoneCall = data?.callViewsByConversationId.get(conversationId)?.phoneCall ?? null;
	const hasResult = Boolean(phoneCall?.coachingAdherenceResult);

	return (
		<section className={css(styleSheet.adherence)} aria-label='Coaching adherence'>
			<h3 className={css(styleSheet.adherenceTitle)}>Coaching Adherence</h3>
			{hasResult && phoneCall ? (
				<CoachingAdherenceScorecard phoneCall={phoneCall} />
			) : (
				<p className={css(styleSheet.adherenceNote)}>No coaching adherence analysis found for this conversation.</p>
			)}
		</section>
	);
}
